import React, { useCallback, useEffect, useState } from 'react';
import { ThemeCtx, ThemeMode, getTheme, themeCssVars, FONT_UI, TYPE, useTheme } from '../theme';
import { BrandLogo } from '../components/BrandLogo';
import { Button, Field, Input, Textarea } from '../components/ui';
import { modApi } from '../components/moderation/moderationApi';

// AS1c · Kontakt och support. /support är publik (support-URL i App Store Connect) och
// formuläret finns också i Profil. Ärendet hamnar i Admin → Support och mejlas till supporten.

export const SUPPORT_PATH = '/support';
export const SUPPORT_EMAIL = 'info@qvenari.com';
export const isSupportPath = (pathname: string) => /^\/(support|kontakt)\/?$/.test(pathname);

export function SupportForm({ defaultEmail, defaultName, onSent }: { defaultEmail?: string | null; defaultName?: string | null; onSent?: () => void }) {
  const T = useTheme();
  const [name, setName] = useState(defaultName ?? '');
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // honungsfält
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Ange din e-postadress så att vi kan svara'); return; }
    if (message.trim().length < 5) { setError('Skriv vad ärendet gäller'); return; }
    setBusy(true); setError('');
    try {
      await modApi.support({ name: name.trim() || undefined, email: email.trim(), subject: subject.trim() || undefined, message: message.trim(), website });
      setSent(true); onSent?.();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  if (sent) {
    return (
      <div role="status" style={{ padding: '16px 0' }}>
        <div style={{ ...TYPE.title, color: T.text, marginBottom: 6 }}>Tack, vi har fått ditt meddelande</div>
        <p style={{ ...TYPE.body, color: T.textSub, margin: 0 }}>Vi svarar till {email.trim()} så snart vi kan, oftast inom ett par vardagar.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <Field label="Namn (valfritt)"><Input value={name} onChange={e => setName(e.target.value)} autoComplete="name" /></Field>
        <Field label="E-post"><Input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required /></Field>
      </div>
      <Field label="Ämne (valfritt)"><Input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} /></Field>
      <Field label="Meddelande" error={error || undefined}>
        <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} maxLength={5000} required />
      </Field>
      {/* Syns inte för människor; botar fyller i det. */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={website} onChange={e => setWebsite(e.target.value)}
        style={{ position: 'absolute', left: -10000, width: 1, height: 1, opacity: 0 }} />
      <div><Button type="submit" loading={busy}>Skicka</Button></div>
    </form>
  );
}

/** Publik sida med eget tema, som integritets- och villkorssidorna. */
export function SupportPage() {
  const [mode, setMode] = useState<ThemeMode>(() => (localStorage.getItem('qvenari_theme') as ThemeMode) || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
  const toggle = useCallback(() => setMode(m => { const n = m === 'dark' ? 'light' : 'dark'; localStorage.setItem('qvenari_theme', n); return n; }), []);
  const T = getTheme(mode);
  useEffect(() => { document.title = 'Kontakt och support – Qvenari'; }, []);
  const back = (e: React.MouseEvent) => {
    if (window.history.length > 1 && document.referrer.startsWith(window.location.origin)) { e.preventDefault(); window.history.back(); }
  };
  let user: { email?: string; name?: string } | null = null;
  try { user = JSON.parse(localStorage.getItem('qvenari_user') || 'null'); } catch { /* ingen sparad användare */ }
  return (
    <ThemeCtx.Provider value={{ mode, toggle }}>
      <style>{`${themeCssVars(T)} * { box-sizing: border-box; } body { margin: 0; background: ${T.bg}; font-family: ${FONT_UI}; }`}</style>
      <main style={{ maxWidth: 680, margin: '0 auto', padding: 'calc(env(safe-area-inset-top, 0px) + 24px) 16px 64px', fontSize: 16, lineHeight: 1.6, color: T.textSub, fontFamily: FONT_UI }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <a href="/" onClick={back} style={{ fontSize: 14, fontWeight: 600, color: T.accentText, textDecoration: 'none' }}>← Tillbaka till Qvenari</a>
          <BrandLogo size={26} />
        </div>
        <h1 style={{ ...TYPE.page, color: T.text, margin: '24px 0 8px' }}>Kontakt och support</h1>
        <p style={{ margin: '0 0 8px' }}>
          Frågor om Qvenari, problem med appen eller ditt konto? Mejla <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: T.accentText, fontWeight: 600 }}>{SUPPORT_EMAIL}</a> eller skriv till oss här.
        </p>
        <p style={{ margin: '0 0 24px', fontSize: 14 }}>
          Vill du anmäla ett inlägg, en bild, en annons eller en användare? Använd <b>⋯</b> → <b>Rapportera</b> vid innehållet i appen, så ser vi direkt vad det gäller.
          Där kan du också blockera användare.
        </p>
        <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 18px' }}>
          <SupportForm defaultEmail={user?.email} defaultName={user?.name} />
        </section>
        <hr style={{ border: 0, borderTop: `1px solid ${T.border}`, margin: '36px 0 16px' }} />
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 14, fontWeight: 600 }}>
          <a href="/integritet" style={{ color: T.accentText }}>Integritetspolicy</a>
          <a href="/villkor" style={{ color: T.accentText }}>Användarvillkor</a>
        </div>
      </main>
    </ThemeCtx.Provider>
  );
}
